// @flow

// $FlowIgnore
import combineEvents from '@redux-beacon/combine-events';
// $FlowIgnore
import GoogleTagManager from '@redux-beacon/google-tag-manager';
// $FlowIgnore
import logger from '@redux-beacon/logger';
import {gtmSplitIOEventsMap} from '@travel-frontend/split-io';
import {defaultInteractionEventsMap} from '@travel-frontend/ui-components';
import {createMiddleware} from 'redux-beacon';

import {
  EVENT_ADD_PAYMENT_INFO,
  EVENT_BDV_SPLIT,
  EVENT_CALL_TO_BOOK,
  EVENT_CARRENTAL_CLICK,
  EVENT_COPY_VOUCHER,
  EVENT_CUSTOMER_IS_TRAVELER,
  EVENT_ECOMMERCE,
  EVENT_LEADS,
  EVENT_EXITLAYER_CLICK,
  EVENT_EXITLAYER_LOAD,
  EVENT_INPUT_ERROR,
  EVENT_INSURANCE_CONFIRM,
  EVENT_PAGE_LOAD,
  EVENT_PAGE_VIEW,
  EVENT_PARTNER_RATING,
  EVENT_PICTURE_GALLERY_CLICK,
  EVENT_PSD2_AUTH,
  EVENT_SCARCITY_CLICK,
  EVENT_SCARCITY_LOAD,
  EVENT_SHOWN_VOUCHER,
  EVENT_SOI_CHANGED,
  EVENT_TOOLTIP,
  EVENT_TRANSFER_CLICK,
  EVENT_TRANSFER_LOADED,
  EVENT_VOUCHER,
} from 'redux/middleware/googleTagManager/actionTypes';
import * as events from 'redux/middleware/googleTagManager/events';
// $FlowIgnore

const queuedEvent = (eventDef: Function, test: Function) => {
  return (action, previousState, nextState) =>
    new Promise((resolve) => {
      const interval = setInterval(() => {
        const isPriorEventPushed = window.dataLayer?.find(test);
        if (isPriorEventPushed) {
          clearInterval(interval);
          resolve(eventDef(action, previousState, nextState));
        }
      }, 10);
    });
};

const originalGtmTarget = new GoogleTagManager();

const gtmTarget = (...args) => {
  // original target throws an error if dataLayer isn't loaded (yet),
  // we don't want that. Lets rather push events onto the stack
  // and assume that it will load eventually.
  // Worst case would be the tracking not working,
  // but that's better than the whole page crashing...
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  return originalGtmTarget(...args);
};

const shouldPush = (item) => item.event === 'gtm.dom';
const isSyncEvent = (key) => key === EVENT_PAGE_LOAD;

const eventsMap = {
  ...gtmSplitIOEventsMap,
  [EVENT_PAGE_LOAD]: events.pageLoadEvent.handler,
  [EVENT_PAGE_VIEW]: events.pageViewEvent.handler,
  [EVENT_CALL_TO_BOOK]: events.callToBookEvent.handler,
  [EVENT_CUSTOMER_IS_TRAVELER]: events.customerIsTravelerEvent.handler,
  [EVENT_EXITLAYER_LOAD]: events.exitLayerEvent.handler,
  [EVENT_EXITLAYER_CLICK]: events.exitLayerEvent.handler,
  [EVENT_PICTURE_GALLERY_CLICK]: events.pictureGalleryEvent.handler,
  [EVENT_ADD_PAYMENT_INFO]: events.addPaymentInfoEvent.handler,
  [EVENT_VOUCHER]: events.voucherEvent.handler,
  [EVENT_ECOMMERCE]: combineEvents(
    events.ecommerceEvent.clearer,
    events.ecommerceEvent.handler
  ),
  [EVENT_VOUCHER]: events.voucherEvent.handler,
  [EVENT_LEADS]: events.leads.handler,
  [EVENT_INPUT_ERROR]: events.inputErrorEvent.handler,
  [EVENT_PSD2_AUTH]: events.psd2AuthEvent.handler,
  [EVENT_INSURANCE_CONFIRM]: events.insuranceConfirmEvent.handler,
  [EVENT_CARRENTAL_CLICK]: events.basicCheckout.handler,
  [EVENT_PARTNER_RATING]: events.partnerRatingEvent.handler,
  [EVENT_TRANSFER_CLICK]: events.basicCheckout.handler,
  [EVENT_TRANSFER_LOADED]: events.basicCheckout.handler,
  [EVENT_SOI_CHANGED]: events.basicCheckout.handler,
  [EVENT_SCARCITY_LOAD]: defaultInteractionEventsMap[EVENT_SCARCITY_LOAD],
  [EVENT_SCARCITY_CLICK]: defaultInteractionEventsMap[EVENT_SCARCITY_CLICK],
  [EVENT_TOOLTIP]: defaultInteractionEventsMap[EVENT_TOOLTIP],
  [EVENT_BDV_SPLIT]: events.bdvSplit.handler,
  [EVENT_COPY_VOUCHER]: events.copyVoucherEvent.handler,
  [EVENT_SHOWN_VOUCHER]: events.userVoucherShownEvent.handler,
};

const eventsQueue = Object.fromEntries(
  Object.entries(eventsMap).map(([key, value]) => [
    key,
    isSyncEvent(key) ? value : queuedEvent(value, shouldPush),
  ])
);

const options = process.env.STAGE === 'prod' ? {} : {logger};

export const middleware: Function = createMiddleware(
  eventsQueue,
  gtmTarget,
  options
);
